<template>
  <div
    class="relative flex h-full flex-row rounded-md bg-gray-50 dark:bg-primary-950"
  >
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      <LoadingComponent v-if="loading" />
      <div
        v-else-if="commissionNote"
        class="box flex h-full flex-row divide-x divide-slate-200 overflow-hidden shadow-xl max-md:flex-col max-md:divide-x-0 max-md:divide-y max-md:overflow-y-auto"
      >
        <!-- as on the page of the note: it only changes once the edition is saved -->
        <CommissionNoteSidePanel :commission-note="commissionNote" />

        <div
          class="flex h-full max-h-full min-h-0 min-w-0 flex-1 flex-col max-md:h-auto max-md:max-h-none max-md:flex-none"
        >
          <div class="mx-3 flex shrink-0 justify-between gap-x-2 border-b py-3">
            <div class="flex flex-row items-center justify-start gap-x-2">
              <BaseButton
                icon="chevron-left"
                size="small"
                inverse-icon-order
                :title="$t('button.back')"
                @click="goToShow"
              />
            </div>
            <div class="flex flex-row items-center justify-end gap-x-2">
              <BaseButton
                color="primary"
                icon="check"
                size="small"
                :display-loader="saving"
                :disabled="saving"
                :title="$t('button.save')"
                @click="save"
              />
            </div>
          </div>
          <div
            class="bg-surface-raised min-h-0 flex-1 overflow-y-auto p-3 max-md:flex-none max-md:overflow-visible"
          >
            <div class="grid-form" data-test="note-form">
              <div>
                <BaseShowEditInput
                  v-model="form.date"
                  type="date"
                  :label="$t('attributes.date')"
                  name="date"
                  :errors="errors.date"
                  edition-mode
                />
              </div>
              <div>
                <SelectLanguages
                  v-model="form.language"
                  :label="$t('attributes.language')"
                  edition-mode
                  customer-language-only
                />
              </div>
              <div class="col-span-2">
                <BaseShowEditTextArea
                  v-model="form.remarks"
                  :label="$t('attributes.remarks')"
                  name="remarks"
                  :errors="errors.remarks"
                  edition-mode
                />
              </div>
              <div class="col-span-2">
                <BaseShowEditTextArea
                  v-model="form.internal_note"
                  :label="$t('attributes.internal_note')"
                  name="internal_note"
                  :errors="errors.internal_note"
                  edition-mode
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommissionNoteLoadError
        v-else-if="loadFailed"
        @retry="fetchCommissionNote"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import SelectLanguages from '@c/InitialStateFormComponent/Enums/SelectLanguages.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import CommissionNoteSidePanel from '@/views/invoicing/show/commissionNote/components/CommissionNoteSidePanel.vue'
import CommissionNoteLoadError from '@/views/invoicing/show/commissionNote/components/CommissionNoteLoadError.vue'

/**
 * Edition of the data of a commission note, on its own page as for an invoice:
 * the left panel stays as it was (read only) and the inputs take the place of
 * the lines, which are changed on the page of the note. Only an open note can
 * be edited, by who manages the notes.
 */
export default {
  name: 'TheCommissionNoteEditPage',
  components: {
    BaseButton,
    CommissionNoteLoadError,
    CommissionNoteSidePanel,
    BaseShowEditInput,
    BaseShowEditTextArea,
    LoadingComponent,
    SelectLanguages
  },
  data() {
    return {
      loading: false,
      loadFailed: false,
      saving: false,
      commissionNote: null,
      form: { date: null, remarks: null, internal_note: null, language: null }
    }
  },
  computed: {
    ...mapGetters({ errors: 'auth/getErrors' }),
    canWrite() {
      return (
        this.$can('manage_commission_notes') ||
        this.$can('manage_commission_settings')
      )
    }
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        // leaving the page empties the param before the component is unmounted
        if (!id) return
        this.fetchCommissionNote()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      processFetchAction: 'commissionNote/fetchCommissionNote',
      processUpdateAction: 'commissionNote/updateCommissionNote'
    }),
    // replace, not push: the edit page must not stay in the history, or going back from the note would bring it back
    goToShow() {
      this.$router.replace({
        name: 'commission_note_show_route',
        params: { id: this.$route.params.id }
      })
    },
    loadNote(note) {
      this.commissionNote = note
      this.form = {
        date: note.date,
        remarks: note.remarks,
        internal_note: note.internal_note,
        language: note.language
      }
    },
    fetchCommissionNote() {
      this.loading = true
      this.loadFailed = false
      const targetId = this.$route.params.id
      this.processFetchAction({ id: targetId })
        .then((response) => {
          if (this.$route.params.id !== targetId) return
          const note = response.commission_note

          // a note that is paid or cancelled, or a user who only reads: nothing to edit
          if (!this.canWrite || !note.is_open) {
            this.goToShow()
            return
          }

          this.loadNote(note)
        })
        .catch((error) => {
          if (this.$route.params.id !== targetId) return
          // a missing or forbidden note leaves the page; any other failure shows a message and a way to try again
          if ([403, 404].includes(error?.response?.status)) {
            this.$router.push({ name: 'commission_note_index_route' })
            return
          }

          this.commissionNote = null
          this.loadFailed = true
        })
        .finally(() => {
          if (this.$route.params.id === targetId) this.loading = false
        })
    },
    // the page stays open, as for an invoice: the left panel shows what was saved, the back button leaves for the lines of the note
    save() {
      this.saving = true
      this.processUpdateAction({ id: this.commissionNote.id, data: this.form })
        .then(
          (response) => {
            this.loadNote(response.commission_note)
            this.$h.toastSuccess(this.$t('toasts.updated'))
          },
          // the request failed: the error is already shown, an error of the handler above is not hidden
          () => {}
        )
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>
